document.addEventListener("DOMContentLoaded", function () {

  // ============================================================
  // GLOBAL VARIABLES
  // ============================================================
  let viewedProductsBuffer = [];

  // ============================================================
  // HELPER: Generate unique Event ID
  // ============================================================
  function generateEventId() {
    return "evt_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
  }

  // ============================================================
  // HELPER: Format price (cent precision)
  // ============================================================
  function formatPrice(value) {
    const num = Number(value);
    return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
  }

  // ============================================================
  // PART 0: OTHER EVENT DATA — SEND FIRST
  // ============================================================
  async function hashData(value) {
    if (!value) return null;
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(value.toString().trim().toLowerCase());
    const hash = await crypto.subtle.digest("SHA-256", dataBuffer);
    return Array.from(new Uint8Array(hash))
      .map(b => b.toString(16).padStart(2, "0"))
      .join("");
  }

  function getCustomerData() {
    const c = window.__customer || {};
    const defaultAddress = c.default_address || c.defaultAddress || {};
    const firstAddress = Array.isArray(c.addresses) ? (c.addresses[0] || {}) : {};

    return {
      id: c.id || null,
      email: c.email || null,
      phone:
        c.phone ||
        defaultAddress.phone ||
        firstAddress.phone ||
        null,
      firstName:
        c.firstName ||
        c.first_name ||
        defaultAddress.first_name ||
        firstAddress.first_name ||
        null,
      lastName:
        c.lastName ||
        c.last_name ||
        defaultAddress.last_name ||
        firstAddress.last_name ||
        null
    };
  }

  async function pushOtherEventDataEarly() {
    if (window.__otherEventDataSent) return;

    const c = getCustomerData();

    const [hashed_email, hashed_phone, hashed_firstname, hashed_lastname] =
      await Promise.all([
        hashData(c.email),
        hashData(c.phone),
        hashData(c.firstName),
        hashData(c.lastName)
      ]);

    const hasAnyData =
      c.id ||
      hashed_email ||
      hashed_phone ||
      hashed_firstname ||
      hashed_lastname;

    if (!hasAnyData) {
      /* console.log("ℹ️ [otherEventData] Gönderilmedi, müşteri verisi yok"); */
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "otherEventData",
      crm_id: c.id || null,
      hashed_email,
      hashed_phone,
      hashed_firstname,
      hashed_lastname
    });

    window.__otherEventDataSent = true;

    /* console.log(
      "👤 [otherEventData] En erken aşamada gönderildi | crm_id:",
      c.id || "null",
      "| phone hash:",
      hashed_phone ? hashed_phone.substring(0, 8) + "..." : "null"
    ); */
  }

  // ============================================================
  // MAIN FUNCTION
  // ============================================================
  function initTracking() {

    // ----------------------------------------------------------
    // PART 1: SLIDER / BANNER TRACKING
    // ----------------------------------------------------------
    const isCartPage = window.location.pathname.includes("/cart");

    if (!isCartPage) {
      const slides = document.querySelectorAll(".js-slider-item:not(.gtm-loaded)");

      if (slides.length > 0) {
        const promotionObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const slide = entry.target;

              const promoData = {
                creative_name:  slide.getAttribute("data-creative-name") || slide.getAttribute("data-promotion-name") || null,
                creative_slot:  slide.getAttribute("data-creative-slot") || null,
                promotion_id:   slide.getAttribute("data-promotion-id")  || null,
                promotion_name: slide.getAttribute("data-promotion-name") || null
              };

              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ ecommerce: null });
              window.dataLayer.push({
                event:     "view_promotion",
                timestamp: Date.now(),
                id:        generateEventId(),
                url:       window.location.href,
                ecommerce: promoData
              });

              observer.unobserve(slide);
            }
          });
        }, { root: null, threshold: 0.5 });

        slides.forEach(slide => {
          if (slide.dataset.gtmBound === "true") return;
          slide.dataset.gtmBound = "true";
        
          promotionObserver.observe(slide);
        
          slide.addEventListener("click", function (e) {
            const link = this.querySelector(".card__whole-link") || this.querySelector("a");
            const url = link ? link.href : null;
            const isNewTab = (link && link.target === "_blank") || e.ctrlKey || e.metaKey;
        
            const promoData = {
              creative_name: this.getAttribute("data-creative-name") || this.getAttribute("data-promotion-name") || null,
              creative_slot: this.getAttribute("data-creative-slot") || null,
              promotion_id: this.getAttribute("data-promotion-id") || null,
              promotion_name: this.getAttribute("data-promotion-name") || null
            };
        
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
              event: "select_promotion",
              timestamp: Date.now(),
              id: generateEventId(),
              url: window.location.href,
              ecommerce: promoData
            });
        
            if (url && !isNewTab) {
              e.preventDefault();
              setTimeout(() => {
                window.location.href = url;
              }, 300);
            }
          });
        });
      }
    } 

    // ----------------------------------------------------------
    // PART 2: PRODUCT LIST TRACKING (view_item_list + select_item)
    // ----------------------------------------------------------
    const products = document.querySelectorAll(".js-datalayer-product:not(.gtm-loaded)");

    if (products.length > 0) {
      const productObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const product = entry.target;

            const originalPrice = formatPrice(
              product.getAttribute("data-product-original-price") || product.getAttribute("data-product-price")
            );

            const data = {
              item_id:        product.getAttribute("data-product-id"),
              item_name:      product.getAttribute("data-product-name"),
              affiliation:    "Zuber",
              coupon:         product.getAttribute("data-product-coupon") || "",
              index: Number(product.getAttribute("data-product-index")) || 1,
              item_brand:     product.getAttribute("data-product-brand") || "Zuber",
              item_category:  product.getAttribute("data-product-category") || "",
              item_category2: product.getAttribute("data-product-category2") || "",
              item_category3: product.getAttribute("data-product-category3") || "",
              item_category4: product.getAttribute("data-product-category4") || "",
              item_category5: product.getAttribute("data-product-category5") || "",
              item_list_id:   product.getAttribute("data-product-list-id") || "",
              item_list_name: product.getAttribute("data-product-list-name") || "",
              item_variant:   product.getAttribute("data-product-variant") || "",
              price:          originalPrice,
              discount:       parseFloat(product.getAttribute("data-product-discount")) || 0.0,
              quantity:       1
            };

            viewedProductsBuffer.push(data);
            observer.unobserve(product);

            if (viewedProductsBuffer.length >= 4) {
              const batch = viewedProductsBuffer.splice(0, 4);
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ ecommerce: null });
              window.dataLayer.push({
                event:     "view_item_list",
                timestamp: Date.now(),
                id:        generateEventId(),
                url:       window.location.href,
                ecommerce: { items: batch }
              });

             /*  console.log("📤 [view_item_list] 4'lü paket gönderildi:", batch.map(i => i.item_name)); */
            }
          }
        });
      }, { root: null, threshold: 0.2 });

      products.forEach(product => {
        product.classList.add("gtm-loaded");
        productObserver.observe(product);

        product.addEventListener("click", function () {
          const originalPrice = formatPrice(
            this.getAttribute("data-product-original-price") || this.getAttribute("data-product-price")
          );

          const data = {
            item_id:        this.getAttribute("data-product-id"),
            item_name:      this.getAttribute("data-product-name"),
            affiliation:    "Zuber",
            coupon:         this.getAttribute("data-product-coupon") || "",
            index: Number(this.getAttribute("data-product-index")) || 1,
            item_brand:     this.getAttribute("data-product-brand") || "Zuber",
            item_category:  this.getAttribute("data-product-category") || "",
            item_category2: this.getAttribute("data-product-category2") || "",
            item_category3: this.getAttribute("data-product-category3") || "",
            item_category4: this.getAttribute("data-product-category4") || "",
            item_category5: this.getAttribute("data-product-category5") || "",
            item_list_id:   this.getAttribute("data-product-list-id") || "",
            item_list_name: this.getAttribute("data-product-list-name") || "",
            item_variant:   this.getAttribute("data-product-variant") || "",
            price:          originalPrice,
            discount:       parseFloat(product.getAttribute("data-product-discount")) || 0.0,
            quantity:       1
          };

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event:     "select_item",
            timestamp: Date.now(),
            id:        generateEventId(),
            url:       window.location.href,
            ecommerce: { items: [data] }
          });

          sessionStorage.setItem("last_list_id", data.item_list_id);
          sessionStorage.setItem("last_list_name", data.item_list_name);

          /* console.log("👆 [select_item] Ürün tıklandı:", data.item_name, "| index:", data.index); */
        });
      });
    }

    // ----------------------------------------------------------
// PART 3: REMOVE FROM CART — button click
// ----------------------------------------------------------
// ----------------------------------------------------------
// PART 3: REMOVE FROM CART — button click
// ----------------------------------------------------------
const removeButtons = document.querySelectorAll(".js-remove-item:not(.gtm-loaded)");

removeButtons.forEach(btn => {
  btn.classList.add("gtm-loaded");

  btn.addEventListener("click", function () {
    const row = this.closest("[data-js-cart-item], .cart-form-item, [data-cart-item]");

    const qtyInput =
      row?.querySelector('input[name="updates[]"]') ||
      row?.querySelector(".qty-selector") ||
      row?.querySelector(".product__quantity");

    const qty = Number(
      qtyInput?.value ||
      row?.getAttribute("data-qty") ||
      this.getAttribute("data-item-quantity") ||
      1
    ) || 1;

    const originalPrice = formatPrice(
      this.getAttribute("data-item-original-price") ||
      row?.getAttribute("data-item-original-price") ||
      this.getAttribute("data-item-price") ||
      row?.getAttribute("data-item-price") ||
      0
    );

    const finalLinePrice = parseFloat(
      this.getAttribute("data-item-final-price") ||
      row?.getAttribute("data-item-final-price") ||
      0
    ) || 0;

    let salePrice = 0;
    let value = 0;

    if (finalLinePrice > 0) {
      value = parseFloat(finalLinePrice.toFixed(2));
      salePrice = parseFloat((finalLinePrice / qty).toFixed(2));
    } else {
      salePrice = formatPrice(
        this.getAttribute("data-item-price") ||
        row?.getAttribute("data-item-price") ||
        0
      );
      value = parseFloat((salePrice * qty).toFixed(2));
    }

    const discount = parseFloat(
      this.getAttribute("data-item-discount") ||
      row?.getAttribute("data-item-discount") ||
      Math.max(0, originalPrice - salePrice)
    ) || 0.0;

    let itemVariant = (
      this.getAttribute("data-item-variant") ||
      row?.getAttribute("data-item-variant") ||
      ""
    ).trim();

    if (!itemVariant || itemVariant === "Default Title" || itemVariant === "Default") {
      itemVariant = "";
    }

    const itemData = {
      item_id:        this.getAttribute("data-item-id") || row?.getAttribute("data-item-id") || "",
      item_name:      this.getAttribute("data-item-name") || row?.getAttribute("data-item-name") || "",
      affiliation:    "Zuber",
      coupon:         this.getAttribute("data-item-coupon") || "",
      index:          Number(this.getAttribute("data-item-index")) || Number(row?.getAttribute("data-line")) || 1,
      item_brand:     this.getAttribute("data-item-brand") || row?.getAttribute("data-item-brand") || "Zuber",
      item_category:  this.getAttribute("data-item-category") || row?.getAttribute("data-item-category") || "",
      item_category2: this.getAttribute("data-item-category2") || row?.getAttribute("data-item-category2") || "",
      item_category3: this.getAttribute("data-item-category3") || row?.getAttribute("data-item-category3") || "",
      item_category4: this.getAttribute("data-item-category4") || row?.getAttribute("data-item-category4") || "",
      item_category5: this.getAttribute("data-item-category5") || row?.getAttribute("data-item-category5") || "",
      item_list_id:   "",
      item_list_name: "Cart",
      item_variant:   itemVariant,
      price:          salePrice,
      discount:       discount,
      quantity:       qty
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "remove_from_cart",
      timestamp: Date.now(),
      id: generateEventId(),
      url: window.location.href,
      ecommerce: {
        currency: window.Shopify?.currency?.active || "TRY",
        value: value,
        items: [itemData]
      }
    });

    /* console.log(
      "🗑️ [remove_from_cart] Ürün silindi - (buton):",
      itemData.item_name,
      "| qty:", qty,
      "| price:", salePrice,
      "| value:", value,
      "| discount:", discount
    ); */
  });
});

// ----------------------------------------------------------
// PART 3b: REMOVE FROM CART
// ----------------------------------------------------------
const decrementButtons = document.querySelectorAll(".qty-minus:not(.gtm-loaded), .js-quantity-decrement:not(.gtm-loaded)");

decrementButtons.forEach(btn => {
  btn.classList.add("gtm-loaded");

  btn.addEventListener("click", function () {
    const row = this.closest("[data-js-cart-item], .cart-form-item, [data-cart-item]");
    if (!row) {
      console.warn("➖ Azaltma butonu: üst satır bulunamadı");
      return;
    }

    const qtyInput =
      row.querySelector('input[name="updates[]"]') ||
      row.querySelector(".qty-selector") ||
      row.querySelector(".product__quantity");

    const currentQty = Number(qtyInput?.value || row.getAttribute("data-qty") || 1);

    if (currentQty !== 1) {
      /* console.log("➖ qty > 1, remove_from_cart tetiklenmedi"); */
      return;
    }

    const originalPrice = formatPrice(
      row.getAttribute("data-item-original-price") ||
      row.querySelector(".js-remove-item")?.getAttribute("data-item-original-price") ||
      row.getAttribute("data-item-price") ||
      row.querySelector(".js-remove-item")?.getAttribute("data-item-price") ||
      0
    );

    const salePrice = formatPrice(
      row.getAttribute("data-item-price") ||
      row.querySelector(".js-remove-item")?.getAttribute("data-item-price") ||
      0
    );

    const discount = parseFloat(
      row.getAttribute("data-item-discount") ||
      row.querySelector(".js-remove-item")?.getAttribute("data-item-discount") ||
      Math.max(0, originalPrice - salePrice)
    ) || 0.0;

    const quantity = currentQty;
    const value = parseFloat((salePrice * quantity).toFixed(2));

    let itemVariant = (
      row.getAttribute("data-item-variant") ||
      row.querySelector(".js-remove-item")?.getAttribute("data-item-variant") ||
      ""
    ).trim();
    if (!itemVariant || itemVariant === "Default Title" || itemVariant === "Default") {
      itemVariant = "";
    }

    const itemData = {
      item_id:        row.getAttribute("data-item-id") || row.querySelector(".js-remove-item")?.getAttribute("data-item-id") || "",
      item_name:      row.getAttribute("data-item-name") || row.querySelector(".js-remove-item")?.getAttribute("data-item-name") || "",
      affiliation:    "Zuber",
      coupon:         row.querySelector(".js-remove-item")?.getAttribute("data-item-coupon") || "",
      index:          Number(row.getAttribute("data-line")) || Number(row.querySelector(".js-remove-item")?.getAttribute("data-item-index")) || 1,
      item_brand:     row.getAttribute("data-item-brand") || row.querySelector(".js-remove-item")?.getAttribute("data-item-brand") || "Zuber",
      item_category:  row.getAttribute("data-item-category") || row.querySelector(".js-remove-item")?.getAttribute("data-item-category") || "",
      item_category2: row.getAttribute("data-item-category2") || row.querySelector(".js-remove-item")?.getAttribute("data-item-category2") || "",
      item_category3: row.getAttribute("data-item-category3") || row.querySelector(".js-remove-item")?.getAttribute("data-item-category3") || "",
      item_category4: row.getAttribute("data-item-category4") || row.querySelector(".js-remove-item")?.getAttribute("data-item-category4") || "",
      item_category5: row.getAttribute("data-item-category5") || row.querySelector(".js-remove-item")?.getAttribute("data-item-category5") || "",
      item_list_id:   "",
      item_list_name: "Cart",
      item_variant:   itemVariant,
      price:          originalPrice,
      discount:       discount,
      quantity:       quantity
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event:     "remove_from_cart",
      timestamp: Date.now(),
      id:        generateEventId(),
      url:       window.location.href,
      ecommerce: {
        currency: window.Shopify?.currency?.active || "TRY",
        value:    value,
        items:    [itemData]
      }
    });

   /*  console.log("🗑️ [remove_from_cart] Ürün silindi:", itemData.item_name, "| qty:", quantity, "| value:", value, "| discount:", discount); */
  });
});

  } // End of initTracking

  // ============================================================
  // PART 4: VIEW CART (view_cart — AJAX)
  // ============================================================
  function handleViewCart() {
    if (!window.location.pathname.includes("/cart")) return;
  
    fetch("/cart.js")
      .then(r => r.json())
      .then(cart => {
        const cartItems = cart.items.map((item, index) => {
          const row =
            document.querySelector(`[data-item-key="${CSS.escape(item.key)}"]`) ||
            document.querySelector(`[data-item-id="${CSS.escape(item.sku || String(item.product_id))}"]`);
  
          const qty = Number(item.quantity) || 1;
  
          const originalUnitPrice = row
            ? formatPrice(row.getAttribute("data-item-original-price"))
            : formatPrice((item.original_price || item.final_price || 0) / 100);
  
          const finalUnitPrice = row
            ? formatPrice(row.getAttribute("data-item-price"))
            : formatPrice((item.final_price || item.price || 0) / 100);
  
          const discount = formatPrice(Math.max(0, originalUnitPrice - finalUnitPrice));
  
          let itemVariant = row?.getAttribute("data-item-variant") || "";
          if (!itemVariant || itemVariant === "Default Title") {
            itemVariant = "Default";
          }
  
          return {
            item_id:        item.sku || String(item.product_id),
            item_name:      row?.getAttribute("data-item-name") || item.product_title,
            affiliation:    "Zuber",
            coupon:         item.discounts?.[0]?.title || "",
            index:          index + 1,
            item_brand:     row?.getAttribute("data-item-brand") || item.vendor || "Zuber",
            item_category:  row?.getAttribute("data-item-category") || item.product_type || "",
            item_category2: row?.getAttribute("data-item-category2") || "",
            item_category3: row?.getAttribute("data-item-category3") || "",
            item_category4: row?.getAttribute("data-item-category4") || "",
            item_category5: row?.getAttribute("data-item-category5") || "",
            item_list_id:   row?.getAttribute("data-item-list-id") || "",
            item_list_name: "Cart",
            item_variant:   itemVariant,
            price:          originalUnitPrice,
            discount:       discount,
            quantity:       qty
          };
        });
  
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event:     "view_cart",
          timestamp: Date.now(),
          id:        generateEventId(),
          url:       window.location.href,
          ecommerce: {
            currency: cart.currency,
            value:    formatPrice(cart.total_price / 100),
            items:    cartItems
          }
        });
  
       /*  console.log(
          "🛒 [view_cart] Gönderildi | Ürün sayısı:",
          cartItems.length,
          "| Toplam:",
          formatPrice(cart.total_price / 100),
          cart.currency,
          cartItems
        ); */
      })
      .catch(err => console.error("❌ Sepet verisi alınamadı:", err));
  }

  // ============================================================
  // PART 5: PRODUCT PAGE (view_item)
  // ============================================================
  function handleViewItem() {
    const isProductPage = window.location.pathname.includes("/products/");
    if (!isProductPage) {
     /*  console.log("ℹ️ Ürün detay sayfası değil, view_item atlandı"); */
      return;
    }

    let productEl = document.querySelector("[data-view-item]");

    if (!productEl) {
      productEl =
        document.querySelector(".js-datalayer-product") ||
        document.querySelector("[data-product-id]") ||
        document.querySelector(".product-item") ||
        document.querySelector(".product");
    }

    if (!productEl) {
     /*  console.warn("⚠️ [view_item] Ürün elementi bulunamadı"); */
      return;
    }

    const originalPrice = parseFloat(
      productEl.getAttribute("data-product-original-price") ||
      productEl.getAttribute("data-product-price") || 0
    ) || 0;

    const salePrice = parseFloat(
      productEl.getAttribute("data-product-price") || 0
    ) || 0;

    const discount = parseFloat(
      productEl.getAttribute("data-product-discount")
    ) || Math.max(0, Number((originalPrice - salePrice).toFixed(2)));

    const itemId =
      productEl.getAttribute("data-product-id") ||
      document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value ||
      null;

    const itemName =
      productEl.getAttribute("data-product-name") ||
      document.querySelector("h1.product__title, h1")?.textContent?.trim() ||
      null;

    const itemBrand = productEl.getAttribute("data-product-brand") || "Zuber";

    const itemCategory  = productEl.getAttribute("data-product-category")  || "";
    const itemCategory2 = productEl.getAttribute("data-product-category2") || "";
    const itemCategory3 = productEl.getAttribute("data-product-category3") || "";
    const itemCategory4 = productEl.getAttribute("data-product-category4") || "";
    const itemCategory5 = productEl.getAttribute("data-product-category5") || "";

    let itemVariant = (productEl.getAttribute("data-product-variant") || "").trim();
    if (!itemVariant || itemVariant === "Default Title" || itemVariant === "Default") {
      itemVariant = "";
    }

    // Read from sessionStorage, if unavailable get collection handle from referrer
    let itemListId = sessionStorage.getItem("last_list_id") || "";
    let itemListName = sessionStorage.getItem("last_list_name") || "";

    if (!itemListId && document.referrer.includes("/collections/")) {
      const handle = document.referrer.split("/collections/")[1].split(/[/?#]/)[0];
      itemListId = handle;
      itemListName = handle.replace(/-/g, " ");
    }

    if (window.__viewItemSent) {
      /* console.log("ℹ️ [view_item] Zaten gönderildi, tekrar atlandı"); */
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "view_item",
      timestamp: Date.now(),
      id: generateEventId(),
      url: window.location.href,
      ecommerce: {
        currency: window.Shopify?.currency?.active || "TRY",
        value: salePrice,
        items: [{
          item_id:        itemId,
          item_name:      itemName,
          affiliation:    "Zuber",
          coupon:         productEl.getAttribute("data-product-coupon") || "",
          index:          1,
          item_brand:     itemBrand,
          item_category:  itemCategory,
          item_category2: itemCategory2,
          item_category3: itemCategory3,
          item_category4: itemCategory4,
          item_category5: itemCategory5,
          item_list_id:   itemListId,
          item_list_name: itemListName,
          item_variant:   itemVariant,
          price:          originalPrice,
          discount:       discount,
          quantity:       1
        }]
      }
    });

    window.__viewItemSent = true;

    /* console.log(
      "🔍 [view_item] Gönderildi:",
      itemName,
      "| original price:", originalPrice,
      "| sale price:", salePrice,
      "| discount:", discount,
      "| variant:", itemVariant,
      "| list_id:", itemListId,
      "| list_name:", itemListName
    ); */
  }

  // ============================================================
  // PART 6: ADD TO CART (add_to_cart)
  // This block will be removed when GTM events go live.
  // ============================================================
  document.addEventListener("submit", function (e) {
    const form = e.target.closest('data-js-product-add-to-cart');
    if (!form) return;

    const variantId = form.querySelector('input[name="id"]')?.value;
    if (!variantId) {
      /* console.warn("⚠️ [add_to_cart] Variant ID bulunamadı"); */
      return;
    }

    const variantsJSON = document.querySelector('product-variants script[type="application/json"]');
    if (!variantsJSON) {
      /* console.warn("⚠️ [add_to_cart] Variants JSON bulunamadı"); */
      return;
    }

    const variants = JSON.parse(variantsJSON.textContent);
    const variant = variants.find(v => String(v.id) === String(variantId));
    if (!variant) {
      console.warn("⚠️ [add_to_cart] Variant eşleşmedi, ID:", variantId);
      return;
    }

    const quantityInput = form.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? Number(quantityInput.value) : 1;

    const salePrice = formatPrice((variant.price || 0) / 100);
    const originalPrice = formatPrice(((variant.compare_at_price || variant.price) || 0) / 100);
    const discount = formatPrice(Math.max(0, originalPrice - salePrice));

    const productWrapper =
      document.querySelector("[data-view-item]") ||
      document.querySelector(".js-datalayer-product") ||
      document.querySelector(".main-product") ||
      document.querySelector("product-page");

    const itemName =
      productWrapper?.getAttribute("data-product-name") ||
      document.querySelector("h1.product__title, h1")?.textContent?.trim() ||
      "";

    const itemBrand =
      productWrapper?.getAttribute("data-product-brand") ||
      variant.vendor ||
      "Zuber";

    const itemCategory =
      productWrapper?.getAttribute("data-product-category") ||
      variant.product_type ||
      "";

    const itemCategory2 =
      productWrapper?.getAttribute("data-product-category2") || "";

    const itemCategory3 =
      productWrapper?.getAttribute("data-product-category3") || "";

    const itemCategory4 =
      productWrapper?.getAttribute("data-product-category4") || "";

    const itemCategory5 =
      productWrapper?.getAttribute("data-product-category5") || "";

    const itemListId =
      productWrapper?.getAttribute("data-product-list-id") || "";

    const itemListName =
      productWrapper?.getAttribute("data-product-list-name") || "";

    const itemVariant =
      variant.title && variant.title !== "Default Title"
        ? variant.title
        : (productWrapper?.getAttribute("data-product-variant") || "Default");

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "add_to_cart",
      timestamp: Date.now(),
      id: generateEventId(),
      url: window.location.href,
      ecommerce: {
        currency: window.Shopify?.currency?.active || "TRY",
        value: formatPrice(salePrice * quantity),
        items: [{
          item_id: variant.sku || String(variant.id),
          item_name: itemName,
          affiliation: "Zuber",
          coupon: "",
          index: 1,
          item_brand: itemBrand,
          item_category: itemCategory,
          item_category2: itemCategory2,
          item_category3: itemCategory3,
          item_category4: itemCategory4,
          item_category5: itemCategory5,
          item_list_id: itemListId,
          item_list_name: itemListName,
          item_variant: itemVariant,
          location_id: "",
          price: originalPrice,
          discount: salePrice,
          google_business_vertical: "retail",
          quantity: quantity
        }]
      }
    });

    /* console.log(
      "🛍️ [add_to_cart] Gönderildi:",
      variant.sku || variant.id,
      "| original price:", originalPrice,
      "| sale price:", salePrice,
      "| discount:", discount,
      "| qty:", quantity
    ); */
  });

  // ============================================================
  // PART 7: ADD TO WISHLIST — REMOVED
  // ============================================================

  // ============================================================
  // TRIGGERS
  // ============================================================
  /* console.log("🚀 GTM Tracking başlatılıyor..."); */

  pushOtherEventDataEarly().finally(() => {
    initTracking();
    handleViewCart();
    handleViewItem();
  });

  const bodyObserver = new MutationObserver(function (mutations) {
    const hasNewNodes = mutations.some(m => m.addedNodes.length > 0);
    if (hasNewNodes) {
      initTracking();
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });

});

if (!customElements.get('fly-cart-effect')) {
  class FlyToCartAnimation extends HTMLElement {
    constructor() {
      super();
      this.speedFactor = 300;
      this.targetSize = 20;
      this.startDelay = 200; 
    }

    connectedCallback() {
      this.speedFactor = parseInt(this.getAttribute('speed')) || 300;
      this.targetSize = parseInt(this.getAttribute('size')) || 20;
      this.startDelay = parseInt(this.getAttribute('delay')) || 200;

      this.handleGlobalClick = this.handleGlobalClick.bind(this);
      document.addEventListener('click', this.handleGlobalClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleGlobalClick);
    }

    // --- UPDATED TARGET FINDER ---
    getVisibleElement(selectors) {
        const elements = document.querySelectorAll(selectors);
        
        for (let el of elements) {
            const rect = el.getBoundingClientRect();
            const style = window.getComputedStyle(el);

            // 1. Does it take up space?
            // 2. Is it not display: none? (offsetParent check)
            // 3. Is it visible? (not opacity 0 or visibility hidden)
            if (rect.width > 0 && 
                rect.height > 0 && 
                el.offsetParent !== null && 
                style.opacity !== '0' && 
                style.visibility !== 'hidden') {
                
                // If this is a button and contains an SVG, return the SVG instead (more precise targeting)
                const innerIcon = el.querySelector('svg, img');
                if (innerIcon) {
                     const iconRect = innerIcon.getBoundingClientRect();
                     // Return the icon if it also has valid dimensions
                     if(iconRect.width > 0 && iconRect.height > 0) return innerIcon;
                }

                return el;
            }
        }
        return null;
    }

    handleGlobalClick(e) {
      const btn = e.target.closest('.add-to-car-collection') || 
                  e.target.closest('.quick-add-to-cart-button button') ||
                  e.target.closest('button[name="add"]') ||
                  e.target.closest('.product-form__submit') || 
                  e.target.closest('.add-to-cart') ||
                  e.target.closest('.sticky-add-to-cart') || 
                  e.target.closest('[id*="sticky"] button');

      if (!btn) return;

      setTimeout(() => {
        this.runFlyAnimation(btn);
      }, this.startDelay);
    }

    runFlyAnimation(btn) {
      let productImg = null;

      // 1. SPECIAL SELECTORS FOR PRODUCT PAGE (PDP) FIRST
      const pdpSelectors = [
          '.product-gallery-item.css-slide-active img',
          '.product-gallery-item.is-active img',
          '.swiper-slide-active img',
          '[id^="FeaturedMedia-"] img'
      ];

      for (let selector of pdpSelectors) {
          const img = document.querySelector(selector);
          // Width check: at least 150px to filter out icons
          if (img && img.offsetWidth > 150 && img.offsetParent !== null) {
              productImg = img;
              break;
          }
      }

      // 2. SELECTOR FOR HOMEPAGE / COLLECTION (GRID)
      if (!productImg) {
          // Go upward from the button and find the nearest product card
          let card = btn.closest('.grid__item, .product-card, .card-wrapper, .product-item, .collection__item');
          if (card) {
              // Choose the largest image in the card excluding Pluxee and badge images
              const allImgs = Array.from(card.querySelectorAll('img'));
              productImg = allImgs.find(img => 
                  !img.src.includes('pluxee') && 
                  !img.src.includes('badge') && 
                  img.offsetWidth > 50
              );
          }
      }

      // 3. FALLBACK: IF NOTHING IS FOUND, USE THE FIRST LOGICAL IMAGE ABOVE THE BUTTON
      if (!productImg) {
          let current = btn;
          for (let i = 0; i < 8; i++) {
              if (!current.parentElement) break;
              current = current.parentElement;
              const img = current.querySelector('img:not([src*="pluxee"]):not([src*="badge"])');
              if (img && img.offsetWidth > 60) {
                  productImg = img;
                  break;
              }
          }
      }

      // --- TARGET CART ELEMENT ---
      const targetSelectors = '.mobile-cart-button, #cart-icon-bubble, .header__icon--cart, a[href="/cart"], .header__cart';
      const finalTarget = this.getVisibleElement(targetSelectors);

      if (!productImg || !finalTarget) {
         /*  console.warn("FlyCart: Görsel veya sepet bulunamadı."); */
          return;
      }

      // Animation code continues below (leave as is)...
      const rectStart = productImg.getBoundingClientRect();
      const rectEnd = finalTarget.getBoundingClientRect();
      
      const targetTop = rectEnd.top + (rectEnd.height / 2) - (this.targetSize / 2);
      const targetLeft = rectEnd.left + (rectEnd.width / 2) - (this.targetSize / 2);

      const distance = Math.sqrt(Math.pow(rectEnd.left - rectStart.left, 2) + Math.pow(rectEnd.top - rectStart.top, 2));
      const duration = Math.max(0.5, distance / this.speedFactor); 

      const flyImg = document.createElement('img');
      flyImg.src = productImg.currentSrc || productImg.src; 

      Object.assign(flyImg.style, {
          position: 'fixed',
          top: rectStart.top + 'px',
          left: rectStart.left + 'px',
          width: rectStart.width + 'px',
          height: rectStart.height + 'px',
          maxWidth: rectStart.width + 'px',
          maxHeight: rectStart.height + 'px',
          zIndex: '2147483647',
          pointerEvents: 'none',
          transition: `all ${duration}s cubic-bezier(0.25, 1, 0.5, 1)`,
          objectFit: 'contain',
          borderRadius: '0px',
          opacity: '0.9'
      });

      document.body.appendChild(flyImg);

      requestAnimationFrame(() => {
          setTimeout(() => {
              flyImg.style.top = targetTop + 'px';
              flyImg.style.left = targetLeft + 'px';
              flyImg.style.width = this.targetSize + 'px';
              flyImg.style.height = this.targetSize + 'px';
              flyImg.style.opacity = '0.5';
          }, 10);
      });

      setTimeout(() => {
          flyImg.remove();
          const targetShake = finalTarget.closest('a') || finalTarget.closest('button') || finalTarget;
          if(targetShake) {
             targetShake.style.transition = 'transform 0.2s';
             targetShake.style.transform = 'scale(1.2)';
             setTimeout(() => targetShake.style.transform = 'scale(1)', 200);
          }
      }, duration * 1000);
  }
  }

  customElements.define('fly-cart-effect', FlyToCartAnimation);
}